import { FC, memo, useRef, useState } from 'react'

import { Element } from 'react-scroll'
import YouTube, {
  YouTubePlayer as YouTubePlayerType,
  YouTubeProps,
} from 'react-youtube'

import { isDevMode } from '@sportworld/js-sdk/src/envs/envs'

import { Button } from '@/components/Button/Button'
import { SettingsHeader } from '@/components/SettingsHeader'
import { focusIds, orientation } from '@/constants/focus'
import { scrollOptions } from '@/constants/scrollers'
import { ScrollContainer, SettingsContainer } from '@/containers/styles'
import { useCustomFocus } from '@/hooks/useCustomFocus'
import { isTVOS } from '@/utils/utils'

import { YouTubePlayerWrapper } from './styles'

const focusId = 'youtube'

export const TestYouTubePlayerRaw: FC = () => {
  const player = useRef<Nullable<YouTubePlayerType>>(null)
  const [isPlaying, setIsPlaying] = useState(false)

  const { defaultFocusChild, onMove } = useCustomFocus({
    focusId,
    scrollOptions: scrollOptions.YouTubeScroll,
  })

  const videoId = 'M7lc1UVf-VE'

  const onPlayerReady: YouTubeProps['onReady'] = async (event) => {
    player.current = event.target
    if (videoId) {
      event.target.cueVideoById(videoId)
    }
    event.target.playVideo()
  }

  const onPlayerError: YouTubeProps['onError'] = async (error) => {
    console.error('onPlayerError', error)
  }

  const onPlayerStateChange: YouTubeProps['onStateChange'] = async (event) => {
    isDevMode && console.log('onPlayerStateChange', event)
  }

  /**
   * @implements
   * https://developers.google.com/youtube/player_parameters
   */
  const opts: YouTubeProps['opts'] = {
    host: 'https://www.youtube-nocookie.com',
    playerVars: {
      autoplay: 0,
      modestbranding: 1,
      playsinline: 1,
      fs: 0,
      origin: isTVOS() ? 'file://' : 'http://localhost:3000',
    },
  }

  const play = () => {
    player.current?.playVideo()
  }

  const pause = () => {
    player.current?.pauseVideo()
  }

  const togglePlay = async () => {
    isPlaying ? player.current?.pauseVideo() : player.current?.playVideo()
    setIsPlaying(!isPlaying)
  }

  const toggleMute = async () => {
    const isMuted = await player.current?.isMuted()
    isMuted ? player.current?.unMute() : player.current?.mute()
  }

  const nextVideo = () => {
    player.current?.nextVideo()
  }

  const previousVideo = () => {
    player.current?.previousVideo()
  }

  return (
    <SettingsContainer
      defaultFocusChild={defaultFocusChild}
      elementType="div"
      orientation={orientation.VERTICAL}
      onMove={onMove}
      className="content-wrapper"
    >
      <SettingsHeader title={'YouTube Player'} />

      <ScrollContainer
        id={scrollOptions.YouTubeScroll.containerId}
        height={scrollOptions.YouTubeScroll.height}
      >
        <YouTubePlayerWrapper>
          <YouTube
            opts={opts}
            onReady={onPlayerReady}
            onError={onPlayerError}
            onStateChange={onPlayerStateChange}
          />
        </YouTubePlayerWrapper>

        <Element name={`${focusIds.BUTTON}Play`}>
          <Button text={'Toggle play'} onClick={togglePlay} expand />
        </Element>
        <Element name={`${focusIds.BUTTON}Play`}>
          <Button text={'Play'} onClick={play} expand />
        </Element>
        <Element name={`${focusIds.BUTTON}Pause`}>
          <Button text={'Pause'} onClick={pause} expand />
        </Element>
        <Element name={`${focusIds.BUTTON}Toggle mute`}>
          <Button text={'Toggle mute'} onClick={toggleMute} expand />
        </Element>
        <Element name={`${focusIds.BUTTON}Next video`}>
          <Button text={'Next video'} onClick={nextVideo} expand />
        </Element>
        <Element name={`${focusIds.BUTTON}Previous video`}>
          <Button text={'Previous video'} onClick={previousVideo} expand />
        </Element>
      </ScrollContainer>
    </SettingsContainer>
  )
}

export const TestYouTubePlayer = memo(TestYouTubePlayerRaw)
